import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Lock, Save, Search, Upload, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import {
  clearAdminSession,
  fetchAdminRankings,
  fetchPlayers,
  loginAdmin,
  saveAdminRankingSettings,
} from "@/lib/api";
import { fmtNum, positionColor } from "@/lib/format";
import {
  applyAdminBlendToPlayers,
  getAdminRankings,
  getBlendWeight,
  playerBlendKey,
  saveAdminRankings as cacheAdminRankings,
  saveBlendWeight,
} from "@/lib/adminRankings";
import { getAdminToken } from "@/lib/api";
import { fetchFantasyCalcValues, mergePlayersWithFantasyCalc } from "@/lib/fantasyCalcMerge";

const POS_FILTERS = ["ALL", "QB", "RB", "WR", "TE", "PICK"];

function orderPlayers(players, savedOrder = []) {
  const byKey = new Map(players.map((player) => [playerBlendKey(player), player]));
  const used = new Set();
  const ordered = [];

  for (const key of savedOrder) {
    const player = byKey.get(key);
    if (player && !used.has(key)) {
      ordered.push(player);
      used.add(key);
    }
  }

  const remaining = players
    .filter((player) => !used.has(playerBlendKey(player)))
    .sort((a, b) => (a.combinedRank || 9999) - (b.combinedRank || 9999));
  return [...ordered, ...remaining];
}

function moveItem(items, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return items;
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

function sameOrder(a = [], b = []) {
  return a.length === b.length && a.every((key, index) => key === b[index]);
}

function normalizeAdminCsvName(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/'/g, "")
    .replace(/-/g, " ")
    .trim();
}

/**
 * Parse CSV with name + optional rank (same shape as FP import: name, rank, position optional).
 * Returns player keys in desired order; unmatched CSV rows are skipped; players not listed append in current order.
 */
function parseAdminRankingsCsvOrder(csvText, allPlayers) {
  const lines = csvText.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);
  if (!lines.length) return null;

  const firstCols = lines[0].split(",").map((c) => c.replace(/^"|"$/g, "").trim().toLowerCase());
  const hasHeader = firstCols.some((h) => ["name", "player", "rank", "position", "pos"].includes(h));
  const dataLines = hasHeader ? lines.slice(1) : lines;

  let nameIdx = 0;
  let rankIdx = null;
  if (hasHeader) {
    const header = lines[0].split(",").map((c) => c.replace(/^"|"$/g, "").trim().toLowerCase());
    for (let i = 0; i < header.length; i += 1) {
      if (header[i] === "name" || header[i] === "player") nameIdx = i;
      else if (header[i] === "rank") rankIdx = i;
    }
  }

  const entries = [];
  let autoRank = 1;
  for (const line of dataLines) {
    const cols = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
    const name = cols[nameIdx];
    if (!name) continue;
    let rankVal = null;
    if (rankIdx !== null && rankIdx < cols.length) {
      const parsed = parseInt(cols[rankIdx], 10);
      rankVal = Number.isFinite(parsed) ? parsed : null;
    }
    if (rankVal === null) rankVal = autoRank;
    autoRank += 1;
    entries.push({ name, rank: rankVal });
  }

  entries.sort((a, b) => a.rank - b.rank);

  const byNorm = new Map();
  for (const p of allPlayers) {
    const key = playerBlendKey(p);
    const nn = normalizeAdminCsvName(p.name);
    if (!byNorm.has(nn)) byNorm.set(nn, []);
    byNorm.get(nn).push(p);
  }

  const orderedKeys = [];
  const used = new Set();
  let matchedFromCsv = 0;
  for (const { name } of entries) {
    const nn = normalizeAdminCsvName(name);
    const candidates = byNorm.get(nn) || [];
    const match = candidates.find((p) => !used.has(playerBlendKey(p)));
    if (match) {
      const k = playerBlendKey(match);
      orderedKeys.push(k);
      used.add(k);
      matchedFromCsv += 1;
    }
  }

  for (const p of allPlayers) {
    const k = playerBlendKey(p);
    if (!used.has(k)) orderedKeys.push(k);
  }

  return { orderedKeys, matchedFromCsv, csvRowCount: entries.length };
}

export function AdminRankingsPage() {
  const [authenticated, setAuthenticated] = useState(() => Boolean(getAdminToken()));
  const [password, setPassword] = useState("");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("ALL");
  const [blendWeight, setBlendWeight] = useState(() => getBlendWeight());
  const [dragKey, setDragKey] = useState(null);
  const [editingRank, setEditingRank] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [fantasyCalcCount, setFantasyCalcCount] = useState(0);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const playerData = await fetchPlayers("superflex");
        if (cancelled) return;
        let fantasyCalc = { byKey: new Map(), byName: new Map(), count: 0 };
        try {
          fantasyCalc = await fetchFantasyCalcValues();
        } catch {
          toast.warning("Using app FantasyCalc values because the live FantasyCalc request was blocked.");
        }
        if (cancelled) return;
        const localRankings = getAdminRankings();
        const localBlendWeight = getBlendWeight();
        const remoteRankings = await fetchAdminRankings("superflex").catch(() => null);
        let savedOrder = remoteRankings?.order || [];
        let currentBlendWeight = Number.isFinite(remoteRankings?.blendWeight) ? remoteRankings.blendWeight : localBlendWeight;

        if (!savedOrder.length && localRankings?.order?.length) {
          savedOrder = localRankings.order;
          currentBlendWeight = localBlendWeight;
          try {
            await saveAdminRankingSettings({ format: "superflex", order: savedOrder, blendWeight: currentBlendWeight });
            toast.success("Migrated local admin rankings to Atlas");
          } catch {
            toast.warning("Using local admin rankings because Atlas save failed.");
          }
        }

        if (savedOrder.length || remoteRankings) {
          cacheAdminRankings(savedOrder);
          saveBlendWeight(currentBlendWeight);
        }

        setFantasyCalcCount(fantasyCalc.count || (playerData.players || []).length);
        const mergedPlayers = mergePlayersWithFantasyCalc(playerData.players || [], fantasyCalc);
        const orderedPlayers = orderPlayers(mergedPlayers, savedOrder);
        setBlendWeight(currentBlendWeight);
        setPlayers(orderedPlayers);
        setSavedSnapshot({
          order: orderedPlayers.map(playerBlendKey),
          blendWeight: currentBlendWeight,
        });
      } catch (error) {
        toast.error("Failed to load admin rankings data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [authenticated]);

  const previewPlayers = useMemo(() => (
    applyAdminBlendToPlayers(players, {
      order: players.map(playerBlendKey),
      blendWeight,
    })
  ), [blendWeight, players]);

  const previewByKey = useMemo(() => {
    const map = new Map();
    for (const player of previewPlayers) map.set(playerBlendKey(player), player);
    return map;
  }, [previewPlayers]);

  const visiblePlayers = useMemo(() => {
    let rows = players;
    if (pos !== "ALL") rows = rows.filter((player) => player.position === pos);
    if (q.trim()) {
      const term = q.trim().toLowerCase();
      rows = rows.filter((player) => (
        player.name.toLowerCase().includes(term) ||
        (player.team || "").toLowerCase().includes(term)
      ));
    }
    return rows;
  }, [players, pos, q]);

  const hasUnsavedChanges = useMemo(() => {
    if (!savedSnapshot) return false;
    return (
      !sameOrder(players.map(playerBlendKey), savedSnapshot.order) ||
      blendWeight !== savedSnapshot.blendWeight
    );
  }, [blendWeight, players, savedSnapshot]);

  const authenticate = async (event) => {
    event.preventDefault();
    try {
      await loginAdmin(password);
      setAuthenticated(true);
      setPassword("");
      toast.success("Admin session started");
    } catch (err) {
      clearAdminSession();
      setAuthenticated(false);
      toast.error(err?.response?.data?.detail || "Incorrect password");
    }
  };

  const moveByKey = (key, direction) => {
    setPlayers((current) => {
      const index = current.findIndex((player) => playerBlendKey(player) === key);
      return moveItem(current, index, index + direction);
    });
  };

  const moveToRank = (key, rankValue) => {
    const nextRank = Number(rankValue);
    if (!Number.isInteger(nextRank) || nextRank < 1 || nextRank > players.length) {
      setEditingRank(null);
      return;
    }

    setPlayers((current) => {
      const fromIndex = current.findIndex((player) => playerBlendKey(player) === key);
      return moveItem(current, fromIndex, nextRank - 1);
    });
    setEditingRank(null);
  };

  const startRankEdit = (key, rank) => {
    setEditingRank({ key, value: String(rank) });
  };

  const dropOnKey = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    setPlayers((current) => {
      const fromIndex = current.findIndex((player) => playerBlendKey(player) === dragKey);
      const toIndex = current.findIndex((player) => playerBlendKey(player) === targetKey);
      return moveItem(current, fromIndex, toIndex);
    });
    setDragKey(null);
  };

  const save = async () => {
    const order = players.map(playerBlendKey);
    try {
      await saveAdminRankingSettings({ format: "superflex", order, blendWeight });
      cacheAdminRankings(order);
      saveBlendWeight(blendWeight);
      setSavedSnapshot({ order, blendWeight });
      toast.success("Admin rankings saved to Atlas");
    } catch {
      toast.error("Failed to save admin rankings to Atlas");
    }
  };

  const updateBlendWeight = (weight) => {
    setBlendWeight(Math.min(1, Math.max(0, Number(weight) || 0)));
  };

  const confirmCancelChanges = () => {
    if (!savedSnapshot) return;
    setPlayers((current) => orderPlayers(current, savedSnapshot.order));
    setBlendWeight(savedSnapshot.blendWeight);
    setEditingRank(null);
    setConfirmCancelOpen(false);
  };

  const handleAdminCsvFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseAdminRankingsCsvOrder(text, players);
      if (!parsed || !parsed.orderedKeys.length) {
        toast.error("Could not parse CSV or file was empty.");
        return;
      }
      if (parsed.matchedFromCsv === 0) {
        toast.error("No CSV rows matched a player by name.");
        return;
      }
      setPlayers((current) => orderPlayers(current, parsed.orderedKeys));
      toast.success(`Loaded ${parsed.matchedFromCsv} player(s) from CSV (not saved).`);
    } catch {
      toast.error("Failed to read CSV file.");
    } finally {
      event.target.value = "";
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#050507] text-white flex items-center justify-center px-6">
        <Toaster position="top-right" richColors />
        <form onSubmit={authenticate} className="w-full max-w-sm border border-[#3F3F46] bg-[#111116] p-6 shadow-[8px_8px_0_0_#000]">
          <div className="flex items-center gap-2 text-[#A78BFA] mb-3">
            <Lock size={16} />
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono-data">Admin Only</span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Rankings editor.</h1>
          <p className="text-sm text-[#A1A1AA] mt-2">Enter the admin password to continue.</p>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-5 w-full bg-[#050507] border border-[#3F3F46] focus:border-[#A78BFA] outline-none px-3 py-3 text-sm text-white font-mono-data"
            placeholder="Password"
            autoFocus
          />
          <button type="submit" className="mt-3 w-full border border-white bg-white text-[#0B0B0F] px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[#A78BFA] hover:border-[#A78BFA] transition-colors">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      <Toaster position="top-right" richColors />
      <header className="border-b border-[#24242A] bg-[#0B0B0F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data">Admin / Rankings</span>
            <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
              TRYSTOMATIC <span className="text-[#A78BFA]">rankings editor</span>.
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-2 max-w-2xl">
              Drag players into your dynasty order, then blend that order with live FantasyCalc values.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setConfirmCancelOpen(true)}
              disabled={!hasUnsavedChanges}
              className="border border-[#3F3F46] text-white px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:border-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#3F3F46] transition-colors"
            >
              Cancel Changes
            </button>
            <button onClick={save} className="border border-white bg-white text-[#0B0B0F] px-4 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[#A78BFA] hover:border-[#A78BFA] transition-colors inline-flex items-center gap-2">
              <Save size={14} /> Save Rankings
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <section className="border border-[#24242A] bg-[#111116] p-4 mb-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data">Blend Weight</div>
              <div className="font-display font-black text-2xl mt-1">Your Rankings ←→ FantasyCalc</div>
              <div className="text-xs text-[#A1A1AA] font-mono-data mt-1">
                {Math.round(blendWeight * 100)}% your rankings / {Math.round((1 - blendWeight) * 100)}% FantasyCalc
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(blendWeight * 100)}
              onChange={(event) => updateBlendWeight(Number(event.target.value) / 100)}
              className="w-full md:w-[360px]"
            />
          </div>
        </section>

        <section className="border border-[#24242A] bg-[#111116]">
          <div className="border-b border-[#24242A] p-3 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Search player or team..."
                className="w-full bg-[#050507] border border-[#3F3F46] focus:border-[#A78BFA] outline-none pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-[#71717A] font-mono-data"
              />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {POS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setPos(filter)}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${pos === filter ? "bg-white text-[#0B0B0F] border-white" : "bg-[#050507] text-white border-[#3F3F46] hover:border-white"}`}
                >
                  {filter}
                </button>
              ))}
              <label className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest border border-[#3F3F46] bg-[#050507] hover:border-white cursor-pointer transition-colors">
                <Upload size={13} />
                Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleAdminCsvFile}
                  data-testid="admin-rankings-csv-upload"
                />
              </label>
            </div>
          </div>

          <div className="overflow-auto max-h-[720px]">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-[#111116] z-10 border-b border-[#24242A]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Rank</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Player</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Pos</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Team</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">FC Value</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Your Value</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Blended</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data">Move</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-3 py-10 text-center text-[#A1A1AA]">Loading rankings...</td></tr>
                ) : visiblePlayers.map((player) => {
                  const key = playerBlendKey(player);
                  const fullRank = players.findIndex((row) => playerBlendKey(row) === key) + 1;
                  const preview = previewByKey.get(key) || player;
                  return (
                    <tr
                      key={key}
                      draggable={editingRank?.key !== key}
                      onDragStart={() => setDragKey(key)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => dropOnKey(key)}
                      className="border-b border-[#24242A] hover:bg-[#18181B] transition-colors"
                    >
                      <td className="px-3 py-2 font-mono-data text-[#A1A1AA] tabular-nums">
                        <span className="inline-flex items-center gap-2">
                          <GripVertical size={13} />
                          {editingRank?.key === key ? (
                            <span className="inline-flex items-center gap-1">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={editingRank.value}
                                onChange={(event) => setEditingRank({ key, value: event.target.value })}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") moveToRank(key, editingRank.value);
                                  if (event.key === "Escape") setEditingRank(null);
                                }}
                                onBlur={() => setEditingRank(null)}
                                className="w-12 bg-[#050507] border border-[#A78BFA] text-white px-1.5 py-0.5 text-xs font-mono-data tabular-nums outline-none"
                                autoFocus
                              />
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setEditingRank(null)}
                                className="border border-[#3F3F46] p-0.5 text-[#A1A1AA] hover:text-white hover:border-[#A78BFA] transition-colors"
                                aria-label="Cancel rank edit"
                              >
                                <X size={11} />
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startRankEdit(key, fullRank)}
                              className="border border-[#3F3F46] px-1.5 py-0.5 text-xs hover:border-[#A78BFA] hover:text-white transition-colors"
                            >
                              #{fullRank}
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold">{player.name}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 ${positionColor(player.position)}`}>{player.position}</span></td>
                      <td className="px-3 py-2 font-mono-data text-xs text-[#A1A1AA]">{player.team || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#A1A1AA]">{fmtNum(preview.fcValue)}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#A78BFA]">{fmtNum(preview.adminRankValue)}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular-nums font-semibold">{fmtNum(preview.combinedValue)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => moveByKey(key, -1)} className="border border-[#3F3F46] p-1 hover:border-white" aria-label="Move up"><ArrowUp size={13} /></button>
                          <button onClick={() => moveByKey(key, 1)} className="border border-[#3F3F46] p-1 hover:border-white" aria-label="Move down"><ArrowDown size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && visiblePlayers.length === 0 && (
                  <tr><td colSpan="8" className="px-3 py-10 text-center text-[#A1A1AA]">No players match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[#24242A] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-mono-data flex items-center justify-between gap-3 flex-wrap">
            <span>{players.length} players loaded</span>
            <span>{fantasyCalcCount} FantasyCalc values matched by Sleeper ID when available</span>
          </div>
        </section>
      </main>
      {confirmCancelOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6">
          <div className="w-full max-w-md border border-[#3F3F46] bg-[#111116] p-5 shadow-[8px_8px_0_0_#000]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data">Confirm Revert</div>
            <h2 className="font-display font-black text-2xl tracking-tight mt-2">Are you sure?</h2>
            <p className="text-sm text-[#A1A1AA] mt-2">All unsaved changes will be lost.</p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmCancelOpen(false)}
                className="border border-[#3F3F46] text-white px-4 py-2 text-xs uppercase tracking-widest hover:border-white transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={confirmCancelChanges}
                className="border border-white bg-white text-[#0B0B0F] px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#A78BFA] hover:border-[#A78BFA] transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
